import { deriveGraph, type GraphTask } from '@agavi/blocks';

import type { Task } from '@/types/experience';

/**
 * Where a task sits in the experience's STRUCTURE, for the editor's prev/next arrows.
 *
 * ## Structural, never flow-driven
 *
 * The arrows walk the experience as it was BUILT, not as a student would be routed through it.
 * Letting flow drive them would make the editor's Back button depend on what a hypothetical
 * student answered — and a Back that does not retrace the Forward you just pressed is one you
 * stop trusting. Preview is the opposite and should be: its Continue/Finish reads the routing
 * outcome off the recorded attempt.
 *
 * ## Built on `nextOf`, which already IS this rule
 *
 * The first version of this file grouped tasks by `pathwayId` and walked array order. That was a
 * second, worse copy of something the package already owns: `deriveGraph`'s `nextOf` is
 * documented as "the one definition of it rather than a second, drifting copy in the panel", and
 * Flow's target picker and the board both read it. Three subtleties it gets right that the
 * hand-rolled version did not:
 *
 * - **Lanes are keyed on `pathwayOf`**, the task a detour hangs off, not on `pathwayId`.
 * - **One lane can hold several independent pathways**, split at each HEAD (a task some goto
 *   points at) — two remediations off the same task must not run into each other.
 * - **An orphaned detour** (its `pathwayOf` points at a task that no longer exists) falls back to
 *   the spine rather than becoming a lane of its own.
 *
 * Walking `nextOf` inherits all three, so the arrows traverse exactly the structure the board
 * draws and the Flow picker lists. The counter counts the same run, which is what lets the two
 * sit beside each other without lying.
 */
export interface TaskTrack {
  /** The contiguous run this task sits in, in structural order. Always contains the task. */
  members: Task[];
  /** 0-based position within `members`. */
  index: number;
  /** Absent at the ends — which is what disables the arrow, rather than a separate flag. */
  prev?: Task;
  next?: Task;
  /** False on the main spine. `colOf` is the redirect depth: 0 is the spine, 1 a detour, 2 a
   *  detour off a detour. */
  onPathway: boolean;
  /** The pathway's name, when it has one and we are on it. */
  pathwayName?: string;
}

/** Guard against a `pathwayOf` cycle, which `computeCol` also defends against. */
const WALK_LIMIT = 512;

/**
 * The run `taskId` sits in, or `null` when the task is not in this experience — which happens
 * while a fork resolves, so the caller renders no arrows rather than guessing at a position.
 */
export function taskTrack(tasks: Task[], taskId: string | undefined): TaskTrack | null {
  if (!taskId) return null;
  const byId = new Map(tasks.map(t => [t.id, t]));
  const current = byId.get(taskId);
  if (!current) return null;

  const graph = deriveGraph(tasks as unknown as GraphTask[]);
  const prevOf = new Map<string, string>();
  for (const [from, to] of Object.entries(graph.nextOf)) if (to) prevOf.set(to, from);

  // Back to the run's first task, then forward collecting it. Both walks are bounded: a
  // malformed experience must not hang the editor's top bar.
  let first = taskId;
  for (let i = 0; i < WALK_LIMIT; i += 1) {
    const back = prevOf.get(first);
    if (!back || back === taskId) break;
    first = back;
  }

  const members: Task[] = [];
  const seen = new Set<string>();
  let cursor: string | null | undefined = first;
  while (cursor && !seen.has(cursor) && members.length < WALK_LIMIT) {
    seen.add(cursor);
    const task = byId.get(cursor);
    if (task) members.push(task);
    cursor = graph.nextOf[cursor];
  }

  const index = members.findIndex(t => t.id === taskId);
  if (index === -1) return null;

  const onPathway = (graph.colOf[taskId] ?? 0) > 0;
  return {
    members,
    index,
    prev: index > 0 ? members[index - 1] : undefined,
    next: index < members.length - 1 ? members[index + 1] : undefined,
    onPathway,
    pathwayName: onPathway ? (current.pathwayName || undefined) : undefined,
  };
}

/**
 * The counter's text: `3/7` on the spine, `↳2/3` on a pathway.
 *
 * FIXED WIDTH is the point, not brevity. These arrows exist to click through tasks quickly, and
 * a counter that grows with a pathway's NAME would move the Next button between tasks — the one
 * thing that makes a stepper unusable. The glyph says "you are on a branch" in one character and
 * never changes width; the name is spelled out where there is room for it, and in the arrows'
 * accessible labels in every mode.
 *
 * TRACK-RELATIVE, deliberately: the denominator counts this run, not the experience. It has to
 * agree with what the arrows do or it is lying about where they can take you. The full shape of
 * the experience belongs in the picker the counter opens.
 */
export function trackCounter(track: TaskTrack): string {
  return `${track.onPathway ? '↳' : ''}${track.index + 1}/${track.members.length}`;
}
